// ** React Imports
import React, { useEffect, useState } from 'react'

// ** Child Imports
import Loader from '../common/loader-components/Loader';
import { OverviewWidgetConfigType } from 'src/lib/types/dashboardAnalytics'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { extractDateFromFilters, getAnalyticsWidgetPayload } from 'src/lib/utils/analyticsHelpers';
import { useLocale } from 'src/lib/hooks/useLocal';
import { analyticsConstants } from 'src/lib/utils/constants';
import { useMerchant } from 'src/lib/contexts/MerchantContext';
import { defaultOverviewWidgetConfig } from 'src/assets/AnalyticsConfig';
import AnalyticsWidgetContainer from './AnalyticsWidgetContainer';
import OrderDataWidget from './dashboard-widgets/OrderDataWidget';
import SalesContributorsTable from './dashboard-widgets/SalesContributorsTable';
import ConversionChart from './dashboard-widgets/ConversionChart';
import ConversionFunnel from './dashboard-widgets/ConversionFunnel';
import C2PConversionDataWidget from './dashboard-widgets/C2PConversionDataWidget';
import PaymentSplit from './dashboard-widgets/PaymentSplitChart';
import SuccessRateChart from './dashboard-widgets/SuccessRateChart';
import DiscountPerformanceWidget from './dashboard-widgets/DiscountPerformanceWidget';
import DiscountFailureRates from './dashboard-widgets/DiscountFailureRatesTable';
import PrefilledAddressWidget from './dashboard-widgets/PrefilledAddressWidget';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface OverviewLayoutBuilderProps {}

const OverviewLayoutBuilder: React.FC<OverviewLayoutBuilderProps> = ({
}) => {

    // Hooks
    const { analyticsFilters } = useFilter();

    const mapAnalyticsWidget = (widgetId: string | string[]) => {

        if (Array.isArray(widgetId) && widgetId?.includes(analyticsConstants.FLO_CONVERSIONS_WITH_DISCOUNTS) || widgetId?.includes(analyticsConstants.FLO_CONVERSIONS_WITHOUT_DISCOUNTS)) {
            widgetId = analyticsConstants.FLO_DISCOUNT_PERFORMANCE;
        }

        switch (widgetId) {
            case `${analyticsConstants.FLO_ORDERS}_total`:
                return <OrderDataWidget id={widgetId} />
            case `${analyticsConstants.FLO_ORDERS}_sales`:
                return <OrderDataWidget id={widgetId}/>
            case `${analyticsConstants.FLO_ORDERS}_aov`:
                return <OrderDataWidget id={widgetId}/>
            case analyticsConstants.FLO_SALES_WIDGET:
                return <SalesContributorsTable 
                        hideBorder
                        hideRowDivider
                        isPaginationEnabled={false}
                        hideHeader
                        hideFooter
                        removeCellPadding
                    />
            case analyticsConstants.FLO_CONVERSION_FUNNEL:
                return <ConversionFunnel />
            case analyticsConstants.FLO_CONVERSION_CHART:
                return <ConversionChart 
                        maxTicksLimit={4}/>
            case analyticsConstants.FLO_COD_TO_PREPAID:
                return <C2PConversionDataWidget />
            case `${analyticsConstants.FLO_PAYMENT_SPLIT}_prepaid_share`:
                return <PaymentSplit id={`${widgetId}`}/>
            case analyticsConstants.FLO_PAYMENT_SPLIT:
                return <PaymentSplit id={widgetId}/>
            case analyticsConstants.FLO_SUCCESS_RATE:
                return <SuccessRateChart />
            case analyticsConstants.FLO_DISCOUNT_PERFORMANCE:
                return <DiscountPerformanceWidget id={widgetId}/>
            case analyticsConstants.FLO_DISCOUNT_FAILURE_RATES:
                return <DiscountFailureRates 
                        id={widgetId}
                        hideBorder
                        hideRowDivider
                        isPaginationEnabled={false}
                        hideHeader
                        hideFooter
                        removeCellPadding 
                    />
            case analyticsConstants.FLO_PREFILLED_ADDRESS_WIDGET:
                return <PrefilledAddressWidget id={widgetId}/>
            default: return <></>
        }
    }

    const renderComponent = (widgetLayoutConfig: OverviewWidgetConfigType) => {
        if(typeof widgetLayoutConfig === "undefined") return <></>;
        return (
            <AnalyticsWidgetContainer
                widgetId={widgetLayoutConfig?.id}
                tooltip={widgetLayoutConfig?.tooltip}
                title={widgetLayoutConfig?.title}
                reportPagePath={widgetLayoutConfig?.reportLink} 
                widgetContent={mapAnalyticsWidget(widgetLayoutConfig?.id)}
                adapter={widgetLayoutConfig?.adapter}
                embededLinks={widgetLayoutConfig?.embededLinks}
                sourceType={widgetLayoutConfig?.sourceType}
                hasComparison={widgetLayoutConfig?.hasComparison}
                APIPayloadObject={[
                    getAnalyticsWidgetPayload( 
                        widgetLayoutConfig?.id,
                        extractDateFromFilters(analyticsFilters, "startDate"),
                        extractDateFromFilters(analyticsFilters, "endDate"),
                        ...(widgetLayoutConfig?.payloadParams ?? [])
                    ),
                    ...(analyticsFilters?.comparison === "previous" && widgetLayoutConfig?.hasComparison ? [getAnalyticsWidgetPayload( 
                        widgetLayoutConfig?.id,
                        extractDateFromFilters(analyticsFilters, "compareToStartDate"),
                        extractDateFromFilters(analyticsFilters, "compareToEndDate"),
                        ...(widgetLayoutConfig?.payloadParams ?? [])
                    )] : [])
                ]}
                // conditionally sending compareToDate props in the AnalyticsWidgetContainer component
                {...((analyticsFilters?.comparison === "previous" && widgetLayoutConfig?.hasComparison) ? {
                    moreIsGood : widgetLayoutConfig?.moreIsGood,
                    ...(widgetLayoutConfig?.widgetType === "table" ?  
                        {comparisonMethod: widgetLayoutConfig?.tableComparison,
                        comparisonColumnIndex: widgetLayoutConfig?.comparisonColumnIndex}
                        : {})
                } : {})}
        />
        )
    }
        

    return (
        <div className='grid gap-4 grid-cols-1 md:grid-cols-3 grid-flow-col-dense'>
            { defaultOverviewWidgetConfig?.map((widgetLayoutConfig: OverviewWidgetConfigType, index: number) => {
                return (
                    <div key={`${index}_${widgetLayoutConfig?.id}`} className={`col-start-1 ${widgetLayoutConfig?.x} ${widgetLayoutConfig?.h} hover:shadow-sm rounded-lg transition-all duration-200 ease-in-out`}>
                        {renderComponent(widgetLayoutConfig)}
                    </div>
                )
            })}
        </div>
    )
}

export default OverviewLayoutBuilder
